import { ChildProfile, AdultProfile } from '@yoyoclub/shared';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/**
 * Get auth token for API requests with retry
 * Retries up to 3 times with 500ms delay if token is not immediately available
 * (handles race condition after OAuth redirect)
 */
async function getAuthHeaders(retries = 3): Promise<HeadersInit> {
  for (let i = 0; i < retries; i++) {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (token) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    }

    // Wait before retry (except on last attempt)
    if (i < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  throw new Error('No authentication token available');
}

// ============================================
// Child Profile API
// ============================================

export interface CreateChildProfileDTO {
  name: string;
  preferredName: string;
  birthDate: string;
  notes?: string;
}

export interface UpdateChildProfileDTO {
  name?: string;
  preferredName?: string;
  birthDate?: string;
  notes?: string;
}

export const childProfileApi = {
  // Get all child profiles for the current user
  getUserProfiles: async (): Promise<ChildProfile[]> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/children`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch child profiles');
    }

    const data = await response.json();
    return data;
  },

  // Get a specific child profile
  getProfile: async (profileId: string): Promise<ChildProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/children/${profileId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch child profile');
    }

    const data = await response.json();
    return data;
  },

  // Create a new child profile
  createProfile: async (profileData: CreateChildProfileDTO): Promise<ChildProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/children`, {
      method: 'POST',
      headers,
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create child profile');
    }

    const data = await response.json();
    return data;
  },

  // Update a child profile
  updateProfile: async (
    profileId: string,
    profileData: UpdateChildProfileDTO
  ): Promise<ChildProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/children/${profileId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update child profile');
    }

    const data = await response.json();
    return data;
  },

  // Delete a child profile
  deleteProfile: async (profileId: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/children/${profileId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete child profile');
    }
  },
};

// ============================================
// Adult Profile API
// ============================================

export interface CreateAdultProfileDTO {
  name: string;
  phone: string;
}

export interface UpdateAdultProfileDTO {
  name?: string;
  phone?: string;
}

export const adultProfileApi = {
  // Get all adult profiles for the current user
  getUserProfiles: async (): Promise<AdultProfile[]> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/adults`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch adult profiles');
    }

    const data = await response.json();
    return data;
  },

  // Get a specific adult profile
  getProfile: async (profileId: string): Promise<AdultProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/adults/${profileId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch adult profile');
    }

    const data = await response.json();
    return data;
  },

  // Create a new adult profile
  createProfile: async (profileData: CreateAdultProfileDTO): Promise<AdultProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/adults`, {
      method: 'POST',
      headers,
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create adult profile');
    }

    const data = await response.json();
    return data;
  },

  // Update an adult profile
  updateProfile: async (
    profileId: string,
    profileData: UpdateAdultProfileDTO
  ): Promise<AdultProfile> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/adults/${profileId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update adult profile');
    }

    const data = await response.json();
    return data;
  },

  // Delete an adult profile
  deleteProfile: async (profileId: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/profiles/adults/${profileId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete adult profile');
    }
  },
};
